import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, User, Mail, Phone, CheckCircle2, ArrowRight, ArrowLeft, PartyPopper } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Reveal from "../components/Reveal";
import PaymentQRs from "../components/PaymentQRs";
import { events, pressFlyer } from "../mock";

const fmtFull = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

const Register = () => {
  const event = events.find((e) => e.featured) || events[0];
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div>
      <section className="pt-32 lg:pt-40 pb-16 bg-secondary/40 relative overflow-hidden">
        <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="container-prc relative max-w-3xl">
          <Link to="/events" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all mb-5">
            <ArrowLeft className="h-4 w-4" /> Back to Events
          </Link>
          <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-5">Event Registration</Badge>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl text-foreground text-balance leading-tight">{event.title}</h1>
          {event.tagline && <p className="mt-4 italic text-primary/80 text-lg">“{event.tagline}”</p>}
        </div>
      </section>

      <section className="py-16">
        <div className="container-prc grid lg:grid-cols-5 gap-10 items-start">
          {/* Event summary */}
          <Reveal className="lg:col-span-2 space-y-6">
            <div className="rounded-[1.5rem] overflow-hidden shadow-soft bg-white">
              <img src={pressFlyer} alt={event.title} className="w-full block" />
            </div>
            <div className="bg-white rounded-[1.5rem] p-6 shadow-soft space-y-3 text-foreground/80">
              <p className="flex items-center gap-3"><Calendar className="h-5 w-5 text-primary shrink-0" /> {fmtFull(event.date)}</p>
              <p className="flex items-center gap-3"><Clock className="h-5 w-5 text-primary shrink-0" /> {event.time}</p>
              <p className="flex items-start gap-3"><MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" /> {event.location}</p>
            </div>
          </Reveal>

          {/* Form / success */}
          <Reveal delay={120} className="lg:col-span-3">
            {submitted ? (
              <div className="bg-white rounded-[1.75rem] p-8 lg:p-12 shadow-lift text-center">
                <span className="grid place-items-center h-20 w-20 mx-auto rounded-full bg-primary/10 text-primary">
                  <PartyPopper className="h-10 w-10" />
                </span>
                <h2 className="font-display font-extrabold text-3xl text-foreground mt-6">You're registered!</h2>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                  Thank you, {form.name.split(" ")[0] || "friend"}! We've saved your spot for the {event.title}. A confirmation will be sent to <span className="font-semibold text-foreground">{form.email}</span>.
                </p>
                <div className="mt-6 inline-flex flex-col gap-2 text-left bg-secondary/50 rounded-2xl p-5 text-sm text-foreground/80">
                  <p className="flex items-center gap-2.5"><Calendar className="h-4 w-4 text-primary" /> {fmtFull(event.date)}</p>
                  <p className="flex items-center gap-2.5"><Clock className="h-4 w-4 text-primary" /> {event.time}</p>
                  <p className="flex items-start gap-2.5"><MapPin className="h-4 w-4 text-primary mt-0.5" /> {event.location}</p>
                </div>
                <p className="mt-6 text-xs text-muted-foreground">Demo registration — details are not saved yet.</p>
                <div className="mt-8 flex flex-wrap gap-4 justify-center">
                  <Button asChild className="btn-cta bg-primary hover:bg-primary/90 rounded-full font-bold px-7 h-12"><Link to="/events">Back to Events</Link></Button>
                  <Button variant="outline" onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "" }); }} className="btn-cta border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full font-bold px-7 h-12">
                    Register Another Person
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-[1.75rem] p-7 lg:p-10 shadow-lift">
                <h2 className="font-display font-extrabold text-2xl text-foreground">Reserve your spot</h2>
                <p className="mt-2 text-muted-foreground">Seating is limited — complete the short form below to register.</p>

                <div className="mt-7 space-y-5">
                  <div>
                    <Label className="font-semibold flex items-center gap-2"><User className="h-4 w-4 text-primary" /> Full name</Label>
                    <Input required value={form.name} onChange={set("name")} className="mt-1.5 h-12 rounded-xl" placeholder="Jane Doe" />
                  </div>
                  <div>
                    <Label className="font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Email address</Label>
                    <Input required type="email" value={form.email} onChange={set("email")} className="mt-1.5 h-12 rounded-xl" placeholder="jane@email.com" />
                  </div>
                  <div>
                    <Label className="font-semibold flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> Phone number</Label>
                    <Input required type="tel" value={form.phone} onChange={set("phone")} className="mt-1.5 h-12 rounded-xl" placeholder="(214) 555-0148" />
                  </div>
                </div>

                <Button type="submit" className="btn-cta w-full mt-8 bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-full h-13 py-3.5 text-base">
                  Complete Registration <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
                <p className="mt-4 text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Registration is {event.price} — pay by Zelle, Venmo, or Cash App (see <Link to="/#" className="underline font-semibold">Ways to Give</Link>)
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      <PaymentQRs subtitle={`Scan a code to pay your ${event.price} registration via Zelle, Venmo, or Cash App.`} />
    </div>
  );
};

export default Register;
